import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { config } from '@/config';
import {
  hasNoDefaultLocation,
  initiLocationValueFromHistory,
} from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { SearchContext } from '@/features/searchV2/types/tracking';
import { promiseWrapper } from '@/helpers';
import { locationSlug } from '@/helpers/urlTs';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import useInitialLoading from '@/hooks/useInitialLoading';
import { _t } from '@/locale';
import { DEALS_SEARCH_ACTION } from '@/reducers/dealsSearchReducer';
import { AppDispatch } from '@/store';
import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { dealsService } from '../../services';

import { dealsSearchPreviewResponseSchema } from '../../types/dealSearch';
import { trackSearchPerformed } from './DealsLanding.helpers';

async function fetchAndSetDeals({
  params,
  context,
  controller,
  dispatch,
}: {
  params?: { location: string; lat?: string; lon?: string };
  context: SearchContext;
  controller: AbortController;
  dispatch: AppDispatch;
}) {
  dispatch({ type: DEALS_SEARCH_ACTION.SET_FETCHING, payload: true });

  const _params: { location?: string; position?: string } = {};

  if (params) {
    if (locationSlug(params.location) === DEFAULT_MY_LOCATION_KEYWORD && params['lat'] && params['lon']) {
      _params['position'] = `${params['lat']}-${params['lon']}`;
      _params['location'] = '';
    } else {
      _params['location'] = params.location ?? '';
    }
  } else if (hasNoDefaultLocation()) {
    _params['location'] = '';
  } else {
    const locationSuggestion = await initiLocationValueFromHistory();
    if (controller.signal.aborted) return;
    if (locationSuggestion?.label) {
      _params['location'] = locationSuggestion.label;
    }
  }

  let response = await promiseWrapper(dealsService.searchLanding({ params: _params, signal: controller.signal }));
  let parsed = dealsSearchPreviewResponseSchema.safeParse(response.data);

  if (parsed.success === false && config.env === 'development') {
    const summary = parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n');
    console.log(summary);
  }

  const isInitialFetch = !params;
  const usedNonEmptyLocation = typeof _params['location'] === 'string' && _params['location'] !== '';
  const isEmptyResult = parsed.success && parsed.data.sections.every((s) => s.items.length === 0);

  if (isInitialFetch && usedNonEmptyLocation && isEmptyResult) {
    const fallbackResponse = await promiseWrapper(
      dealsService.searchLanding({
        params: { location: '' },
        signal: controller.signal,
      }),
    );

    if (controller.signal.aborted) return;

    const fallbackParsed = dealsSearchPreviewResponseSchema.safeParse(fallbackResponse.data);
    if (!fallbackResponse.error && fallbackParsed.success) {
      response = fallbackResponse;
      parsed = fallbackParsed;
    }
  }

  if (response.error || parsed.success === false) {
    if (response?.error?.name !== 'AbortError') {
      Sentry.captureException(response?.error);
      toast(
        ({ closeToast }) => (
          <Snackbar type="danger" label={_t('serverError')} action={<button onClick={closeToast}>OK</button>} />
        ),
        { autoClose: 3000 },
      );
      dispatch({ type: DEALS_SEARCH_ACTION.SET_FETCHING, payload: false });
    }
    return;
  }

  dispatch({
    type: DEALS_SEARCH_ACTION.SET_PREVIEW_RESULTS,
    payload: { preview: parsed.data },
  });

  trackSearchPerformed(_params?.location ?? '', context, parsed.data);
}

export function useDealsLandingPage() {
  const dispatch = useAppDispatch();
  const deals = useAppSelector((state) => state.dealsSearch);
  const isLoading = useInitialLoading(deals.fetching, deals.pendingSSRHydration);
  const fetchControllerRef = useRef<AbortController | null>(null);
  const location = useLocation<{ searchContext?: SearchContext }>();

  const startFetch = (params?: { location: string; lat?: string; lon?: string }) => {
    fetchControllerRef.current?.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;
    const context: SearchContext = location.state?.searchContext || 'free_search';

    fetchAndSetDeals({
      params,
      context,
      controller,
      dispatch,
    });
  };

  useEffect(() => {
    if (!deals.pendingSSRHydration) {
      startFetch();
    } else {
      dispatch({ type: DEALS_SEARCH_ACTION.CONSUME_SSR_HYDRATION });

      const ssrLocationName = deals.preview?.sections?.[0]?.searchParams?.location?.name || '';
      trackSearchPerformed(ssrLocationName, 'navigation', deals.preview);
    }

    return () => {
      fetchControllerRef.current?.abort();
      dispatch({ type: DEALS_SEARCH_ACTION.SET_RESET });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { deals: deals.preview, isLoading };
}
